/**
 * Export employee salary structure breakdown to Excel (exceljs) and PDF (jspdf).
 */
import ExcelJS from "exceljs";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const INR = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return `₹${v.toLocaleString("en-IN")}`;
};

function safeFilePart(s) {
  return String(s || "employee")
    .replace(/[^\w-]+/g, "_")
    .replace(/_+/g, "_")
    .slice(0, 40);
}

function buildFileBase(employee, structureName) {
  const code = employee?.employeeCode || employee?.empCode || employee?.id || "emp";
  const name = [employee?.firstName, employee?.lastName].filter(Boolean).join("_") || "Employee";
  const stamp = new Date().toISOString().slice(0, 10);
  return `${safeFilePart(code)}_${safeFilePart(name)}_${safeFilePart(structureName)}_Salary_Breakdown_${stamp}`;
}

function onOff(v) {
  return v ? "On" : "Off";
}

function collectSectionRows(breakdown) {
  const rows = [];
  const addBlock = (title, list, totalLabel, totalMonthly, totalAnnual) => {
    rows.push({ kind: "section", name: title });
    (list || []).forEach((item) => {
      rows.push({
        kind: "item",
        name: item.name,
        monthly: item.amount,
        annual: item.annualAmount ?? (Number(item.amount) || 0) * 12,
      });
    });
    rows.push({
      kind: "total",
      name: totalLabel,
      monthly: totalMonthly,
      annual: totalAnnual,
    });
  };

  const t = breakdown.totals || {};
  addBlock(
    "Salary Breakdown (E) — Earnings",
    breakdown.earningsList,
    "Subtotal (E)",
    t.earnings,
    (t.earnings || 0) * 12
  );
  addBlock(
    "Reimbursements (R)",
    breakdown.reimbursementsList,
    "Total Reimbursements (R)",
    t.reimbursements,
    (t.reimbursements || 0) * 12
  );
  addBlock(
    "Employer Contributions (B)",
    [...(breakdown.employerList || []), ...(breakdown.statutoryEmployerList || [])],
    "Total Employer (B)",
    t.employerShares,
    t.employerSharesAnnual
  );
  addBlock(
    "Deductions (D)",
    [...(breakdown.deductionsList || []), ...(breakdown.statutoryDeductionsList || [])],
    "Total Deductions (D)",
    t.deductions,
    t.deductionsAnnual
  );
  return rows;
}

function headerInfo(employee, structure, breakdown) {
  const basis = breakdown.calculationBasis || {};
  const appl = breakdown.applicability || {};
  const empName = [employee?.firstName, employee?.lastName].filter(Boolean).join(" ") || "—";
  return {
    empName,
    empCode: employee?.employeeCode || employee?.empCode || "—",
    structureName: structure?.name || breakdown.structureName || "—",
    mode: basis.mode || "CTC",
    profileAnnual: basis.profileAnnual ?? breakdown.annualAmount,
    profileMonthly: basis.profileMonthly ?? breakdown.monthlyCtc,
    effectiveMonth: basis.effectiveMonth || breakdown.effectiveMonth || "—",
    ptState: basis.ptState || breakdown.ptState || "—",
    statutoryLine: `PF: ${onOff(appl.pfApplicable)}, Ceiling PF: ${onOff(appl.ceilingPf)}, ESI: ${onOff(appl.esiApplicable)}${breakdown.esiAutoEnabled ? " (auto)" : ""}, PT: ${onOff(appl.ptApplicable)}, Gratuity: ${onOff(appl.gratuityApplicable)}`,
    basis,
    appl,
  };
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function styleHeaderRow(row) {
  row.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 11 };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF1E3A5F" },
    };
    cell.alignment = { vertical: "middle", wrapText: true };
  });
  row.height = 22;
}

function moneyCell(cell, formulaOrValue, isFormula = false) {
  if (isFormula) {
    cell.value = { formula: String(formulaOrValue).replace(/^=/, "") };
  } else {
    cell.value = Number(formulaOrValue) || 0;
  }
  cell.numFmt = "#,##0";
}

function labelFill(row, argb) {
  row.eachCell((c) => {
    c.fill = { type: "pattern", pattern: "solid", fgColor: { argb } };
  });
}

/**
 * Build Excel with live formulas:
 * - Inputs sheet: rates, wage bases, PF/Gratuity/ESI formulas
 * - Breakdown sheet: annual = monthly×12, section SUM, statutory via Inputs refs
 * - Component Basis sheet: how each line was derived (+ Excel formula text)
 */
export async function exportSalaryBreakdownToExcel({ employee, structure, breakdown }) {
  if (!breakdown) throw new Error("No breakdown to export");
  const info = headerInfo(employee, structure, breakdown);
  const basis = info.basis;
  const rates = basis.rates || {};
  const appl = info.appl || {};
  const wb = new ExcelJS.Workbook();
  wb.creator = "PaceHRM";
  wb.created = new Date();

  // ═══════════════════════════════════════════════════════════
  // Sheet: Inputs (editable drivers + statutory Excel formulas)
  // ═══════════════════════════════════════════════════════════
  const wi = wb.addWorksheet("Inputs", { views: [{ state: "frozen", ySplit: 1 }] });
  wi.columns = [
    { header: "Parameter", key: "p", width: 42 },
    { header: "Value", key: "v", width: 18 },
    { header: "Excel Formula / Note", key: "n", width: 62 },
  ];
  styleHeaderRow(wi.getRow(1));

  // Row map for Inputs (1-based). We'll add sequentially and record keys.
  const I = {}; // named row numbers
  const addInput = (label, value, note, opts = {}) => {
    const r = wi.addRow([label, value, note || ""]);
    const rowNum = r.number;
    if (opts.key) I[opts.key] = rowNum;
    r.getCell(1).font = { bold: true };
    if (opts.section) {
      r.font = { bold: true, color: { argb: "FF1E3A5F" } };
      labelFill(r, "FFDBEAFE");
      r.getCell(2).value = "";
    } else if (opts.formula) {
      moneyCell(r.getCell(2), opts.formula, true);
      r.getCell(2).font = { color: { argb: "FF1D4ED8" } };
    } else if (opts.pct) {
      r.getCell(2).value = Number(value) || 0;
      r.getCell(2).numFmt = "0.00";
    } else if (opts.flag) {
      r.getCell(2).value = value ? 1 : 0;
      r.getCell(3).value = `${note || ""} (1 = On, 0 = Off)`;
    } else if (typeof value === "number") {
      moneyCell(r.getCell(2), value, false);
    }
    if (opts.editable) {
      r.getCell(2).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFFFFBEB" },
      };
    }
    return rowNum;
  };

  addInput("— Profile —", "", "", { section: true });
  addInput("Employee", `${info.empName} (${info.empCode})`, "Read-only");
  addInput("Structure", info.structureName, "Read-only");
  addInput("Mode", info.mode, "CTC or Gross");
  addInput("Profile Annual (₹)", Number(info.profileAnnual) || 0, "Edit to recalculate monthly", {
    key: "annual",
    editable: true,
  });
  addInput(
    "Profile Monthly (₹)",
    0,
    "ROUND(Annual/12, 0)",
    { key: "monthly", formula: `ROUND(B${I.annual}/12,0)` }
  );
  addInput("Effective Month", info.effectiveMonth, "");
  addInput("PT State", info.ptState, "Professional Tax slabs");

  addInput("— Statutory flags (1=On, 0=Off) —", "", "", { section: true });
  addInput("PF Applicable", !!appl.pfApplicable, "Employee profile", { key: "pfOn", flag: true, editable: true });
  addInput("Ceiling PF", !!appl.ceilingPf, "Cap PF wage at PF Wage Ceiling", {
    key: "ceilingOn",
    flag: true,
    editable: true,
  });
  addInput("ESI Applicable", !!appl.esiApplicable, "", { key: "esiOn", flag: true, editable: true });
  addInput("PT Applicable", !!appl.ptApplicable, "", { key: "ptOn", flag: true, editable: true });
  addInput("Gratuity Applicable", !!appl.gratuityApplicable, "", {
    key: "gratuityOn",
    flag: true,
    editable: true,
  });

  addInput("— Wage bases (₹) —", "", "", { section: true });
  addInput(
    "PF Wage Base",
    Number(basis.pfWageBase) || 0,
    "Sum of PF-marked components (else Basic)",
    { key: "pfBase", editable: true }
  );
  addInput(
    "PF Wage Ceiling",
    Number(rates.pfWageCeiling) || 15000,
    "From Statutory Rates",
    { key: "pfCeiling", editable: true }
  );
  addInput(
    "EDLI Wage Ceiling",
    Number(rates.edliWageCeiling) || 15000,
    "From Statutory Rates",
    { key: "edliCeiling", editable: true }
  );
  addInput(
    "PF Wage Used",
    0,
    "IF(Ceiling PF, MIN(PF Base, Ceiling), PF Base)",
    {
      key: "pfUsed",
      formula: `IF(B${I.ceilingOn}=1,MIN(B${I.pfBase},B${I.pfCeiling}),B${I.pfBase})`,
    }
  );
  addInput(
    "Gratuity Wage Base",
    Number(basis.gratuityWageBase) || 0,
    "Usually Basic",
    { key: "gratBase", editable: true }
  );
  addInput(
    "Gross for ESI / PT",
    Number(basis.grossForEsiPt) || 0,
    "Earnings + reimbursements (+ benefits)",
    { key: "grossEsi", editable: true }
  );

  addInput("— Rates from Statutory Rates Manager —", "", "", { section: true });
  addInput("PF Employee Share %", Number(rates.pfEmployeeShare) || 12, "", {
    key: "pfEmpPct",
    pct: true,
    editable: true,
  });
  addInput("PF Employer Share %", Number(rates.pfEmployerShare) || 12, "", {
    key: "pfErPct",
    pct: true,
    editable: true,
  });
  addInput("Admin Charges %", Number(rates.pfAdminCharges) ?? 0.5, "", {
    key: "adminPct",
    pct: true,
    editable: true,
  });
  addInput("EDLI Rate %", Number(rates.pfEdliRate) ?? 0.5, "Capped at ₹75/mo", {
    key: "edliPct",
    pct: true,
    editable: true,
  });
  addInput("Gratuity Rate %", Number(rates.gratuityRate) || 4.81, "", {
    key: "gratPct",
    pct: true,
    editable: true,
  });
  addInput("ESI Employee %", Number(rates.esiEmployeeShare) || 0.75, "", {
    key: "esiEmpPct",
    pct: true,
    editable: true,
  });
  addInput("ESI Employer %", Number(rates.esiEmployerShare) || 3.25, "", {
    key: "esiErPct",
    pct: true,
    editable: true,
  });
  addInput("ESI Cutoff ₹", Number(rates.esiCutoff) || 21000, "", {
    key: "esiCutoff",
    editable: true,
  });
  addInput("PT Amount (monthly) ₹", (() => {
    const pt = (breakdown.statutoryDeductionsList || []).find((x) =>
      /professional|PT/i.test(String(x.name))
    );
    return Number(pt?.amount) || 0;
  })(), "From PT slabs for state (edit if needed)", {
    key: "ptAmt",
    editable: true,
  });

  addInput("— Computed statutory amounts (Excel formulas) —", "", "", { section: true });
  addInput(
    "PF (Employee)",
    0,
    "IF(PF On, ROUND(PF Wage Used × Employee% / 100, 0), 0)",
    {
      key: "pfEmpAmt",
      formula: `IF(B${I.pfOn}=1,ROUND(B${I.pfUsed}*B${I.pfEmpPct}/100,0),0)`,
    }
  );
  addInput(
    "PF Employer Share (12%)",
    0,
    "IF(PF On, ROUND(PF Wage Used × Employer% / 100, 0), 0)",
    {
      key: "pfErShare",
      formula: `IF(B${I.pfOn}=1,ROUND(B${I.pfUsed}*B${I.pfErPct}/100,0),0)`,
    }
  );
  addInput(
    "Admin Charges",
    0,
    "IF(PF On, ROUND(PF Wage Used × Admin% / 100, 0), 0)",
    {
      key: "adminAmt",
      formula: `IF(B${I.pfOn}=1,ROUND(B${I.pfUsed}*B${I.adminPct}/100,0),0)`,
    }
  );
  addInput(
    "EDLI",
    0,
    "IF(PF On, MIN(ROUND(MIN(PF Base, EDLI Ceiling)×EDLI%/100,0), 75), 0)",
    {
      key: "edliAmt",
      formula: `IF(B${I.pfOn}=1,MIN(ROUND(MIN(B${I.pfBase},B${I.edliCeiling})*B${I.edliPct}/100,0),75),0)`,
    }
  );
  addInput(
    "PF (Employer) total",
    0,
    "Employer Share + Admin + EDLI",
    {
      key: "pfErTotal",
      formula: `B${I.pfErShare}+B${I.adminAmt}+B${I.edliAmt}`,
    }
  );
  addInput(
    "Gratuity",
    0,
    "IF(Gratuity On, ROUND(Gratuity Base × Rate% / 100, 0), 0)",
    {
      key: "gratAmt",
      formula: `IF(B${I.gratuityOn}=1,ROUND(B${I.gratBase}*B${I.gratPct}/100,0),0)`,
    }
  );
  addInput(
    "ESI (Employee)",
    0,
    "IF(ESI On AND Eligibility≤Cutoff, ROUND(MIN(Gross,Cutoff)×Emp%/100,0), 0)",
    {
      key: "esiEmpAmt",
      formula: `IF(AND(B${I.esiOn}=1,B${I.grossEsi}<=B${I.esiCutoff}),ROUND(MIN(B${I.grossEsi},B${I.esiCutoff})*B${I.esiEmpPct}/100,0),0)`,
    }
  );
  addInput(
    "ESI (Employer)",
    0,
    "IF(ESI On AND Eligibility≤Cutoff, ROUND(MIN(Gross,Cutoff)×Er%/100,0), 0)",
    {
      key: "esiErAmt",
      formula: `IF(AND(B${I.esiOn}=1,B${I.grossEsi}<=B${I.esiCutoff}),ROUND(MIN(B${I.grossEsi},B${I.esiCutoff})*B${I.esiErPct}/100,0),0)`,
    }
  );
  addInput(
    "Professional Tax (PT)",
    0,
    "IF(PT On, PT Amount, 0)",
    {
      key: "ptFinal",
      formula: `IF(B${I.ptOn}=1,B${I.ptAmt},0)`,
    }
  );

  wi.getColumn(2).width = 18;
  wi.getColumn(3).width = 68;

  // ═══════════════════════════════════════════════════════════
  // Sheet: Breakdown (component amounts + SUM / ×12 formulas)
  // ═══════════════════════════════════════════════════════════
  const ws = wb.addWorksheet("Salary Breakdown", {
    views: [{ state: "frozen", ySplit: 1 }],
  });
  ws.columns = [
    { header: "Section / Component", width: 36 },
    { header: "Monthly (₹)", width: 16 },
    { header: "Annual (₹)", width: 16 },
    { header: "Excel Formula", width: 48 },
  ];
  styleHeaderRow(ws.getRow(1));

  // Meta header
  [
    ["Employee", `${info.empName} (${info.empCode})`],
    ["Structure", info.structureName],
    ["Mode", info.mode],
    ["Statutory", info.statutoryLine],
    ["Note", "Yellow cells are inputs. Blue cells are Excel formulas. Edit Inputs sheet to recalculate PF/ESI/Gratuity."],
  ].forEach(([k, v]) => {
    const r = ws.addRow([k, v, "", ""]);
    r.getCell(1).font = { bold: true };
    labelFill(r, "FFF1F5F9");
  });
  ws.addRow([]);

  const earnRows = [];
  const reimbRows = [];
  const erRows = [];
  const dedRows = [];

  const pushSection = (title) => {
    const r = ws.addRow([title, "", "", ""]);
    r.font = { bold: true, size: 11 };
    labelFill(r, "FFDBEAFE");
  };

  const pushItem = (name, monthly, formulaNote, opts = {}) => {
    const r = ws.addRow([name, null, null, formulaNote || ""]);
    const rowNum = r.number;
    if (opts.inputRef) {
      // Monthly from Inputs sheet formula
      moneyCell(r.getCell(2), opts.inputRef, true);
      r.getCell(2).font = { color: { argb: "FF1D4ED8" } };
    } else {
      moneyCell(r.getCell(2), monthly, false);
      r.getCell(2).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFFFFBEB" },
      };
    }
    // Annual = Monthly × 12
    moneyCell(r.getCell(3), `B${rowNum}*12`, true);
    r.getCell(3).font = { color: { argb: "FF1D4ED8" } };
    if (!formulaNote) {
      r.getCell(4).value = opts.inputRef
        ? `Monthly → ${opts.inputRef}; Annual → =B${rowNum}*12`
        : `Monthly (structure amount); Annual → =B${rowNum}*12`;
    } else {
      r.getCell(4).value = `${formulaNote}; Annual → =B${rowNum}*12`;
    }
    return rowNum;
  };

  const pushTotal = (label, sumRows) => {
    const r = ws.addRow([label, null, null, ""]);
    const rowNum = r.number;
    r.font = { bold: true };
    labelFill(r, "FFE2E8F0");
    if (sumRows.length) {
      const refs = sumRows.map((n) => `B${n}`).join("+");
      const refsC = sumRows.map((n) => `C${n}`).join("+");
      moneyCell(r.getCell(2), refs, true);
      moneyCell(r.getCell(3), refsC, true);
      r.getCell(2).font = { bold: true, color: { argb: "FF1D4ED8" } };
      r.getCell(3).font = { bold: true, color: { argb: "FF1D4ED8" } };
      r.getCell(4).value = `=SUM of component rows → ${refs}`;
    } else {
      moneyCell(r.getCell(2), 0, false);
      moneyCell(r.getCell(3), 0, false);
    }
    return rowNum;
  };

  pushSection("Salary Breakdown (E) — Earnings");
  (breakdown.earningsList || []).forEach((item) => {
    const calc = (breakdown.componentCalculations || []).find(
      (c) => String(c.name).toLowerCase() === String(item.name).toLowerCase()
    );
    earnRows.push(
      pushItem(item.name, item.amount, calc?.basis || "From salary structure configuration")
    );
  });
  const earnTotalRow = pushTotal("Subtotal (E)", earnRows);

  pushSection("Reimbursements (R)");
  (breakdown.reimbursementsList || []).forEach((item) => {
    const calc = (breakdown.componentCalculations || []).find(
      (c) => String(c.name).toLowerCase() === String(item.name).toLowerCase()
    );
    reimbRows.push(
      pushItem(item.name, item.amount, calc?.basis || "Fixed reimbursement")
    );
  });
  const reimbTotalRow = pushTotal("Total Reimbursements (R)", reimbRows);

  pushSection("Employer Contributions (B)");
  (breakdown.employerList || []).forEach((item) => {
    erRows.push(pushItem(item.name, item.amount, "Employer benefit component"));
  });
  (breakdown.statutoryEmployerList || []).forEach((item) => {
    const n = String(item.name);
    if (/PF/i.test(n)) {
      erRows.push(
        pushItem(item.name, item.amount, "Inputs! PF (Employer) total", {
          inputRef: `Inputs!B${I.pfErTotal}`,
        })
      );
    } else if (/Gratuity/i.test(n)) {
      erRows.push(
        pushItem(item.name, item.amount, "Inputs! Gratuity", {
          inputRef: `Inputs!B${I.gratAmt}`,
        })
      );
    } else if (/ESI/i.test(n)) {
      erRows.push(
        pushItem(item.name, item.amount, "Inputs! ESI (Employer)", {
          inputRef: `Inputs!B${I.esiErAmt}`,
        })
      );
    } else {
      erRows.push(pushItem(item.name, item.amount, "Statutory employer"));
    }
  });
  const erTotalRow = pushTotal("Total Employer (B)", erRows);

  pushSection("Deductions (D)");
  (breakdown.deductionsList || []).forEach((item) => {
    dedRows.push(pushItem(item.name, item.amount, "Structure deduction"));
  });
  (breakdown.statutoryDeductionsList || []).forEach((item) => {
    const n = String(item.name);
    if (/PF/i.test(n)) {
      dedRows.push(
        pushItem(item.name, item.amount, "Inputs! PF (Employee)", {
          inputRef: `Inputs!B${I.pfEmpAmt}`,
        })
      );
    } else if (/ESI/i.test(n)) {
      dedRows.push(
        pushItem(item.name, item.amount, "Inputs! ESI (Employee)", {
          inputRef: `Inputs!B${I.esiEmpAmt}`,
        })
      );
    } else if (/PT|Professional/i.test(n)) {
      dedRows.push(
        pushItem(item.name, item.amount, "Inputs! Professional Tax", {
          inputRef: `Inputs!B${I.ptFinal}`,
        })
      );
    } else {
      dedRows.push(pushItem(item.name, item.amount, "Statutory deduction"));
    }
  });
  const dedTotalRow = pushTotal("Total Deductions (D)", dedRows);

  ws.addRow([]);
  const summaryStart = ws.rowCount + 1;

  const addSummary = (label, monthlyFormula, note) => {
    const r = ws.addRow([label, null, null, note]);
    const rowNum = r.number;
    r.font = { bold: true };
    labelFill(r, "FFDCFCE7");
    moneyCell(r.getCell(2), monthlyFormula, true);
    moneyCell(r.getCell(3), `B${rowNum}*12`, true);
    r.getCell(2).font = { bold: true, color: { argb: "FF1D4ED8" } };
    r.getCell(3).font = { bold: true, color: { argb: "FF1D4ED8" } };
    return rowNum;
  };

  addSummary(
    "Gross Salary (E + R)",
    `B${earnTotalRow}+B${reimbTotalRow}`,
    `=Subtotal(E)+Reimbursements(R)`
  );
  const grossRow = summaryStart;
  addSummary(
    "Net Pay (E + R − D)",
    `B${grossRow}-B${dedTotalRow}`,
    `=Gross − Deductions`
  );
  addSummary(
    "Total CTC (E + R + B)",
    `B${grossRow}+B${erTotalRow}`,
    `=Gross + Employer`
  );
  const profileRow = ws.addRow([
    "Profile CTC Target",
    null,
    null,
    "From Inputs! Profile Monthly / Annual",
  ]);
  profileRow.font = { bold: true };
  labelFill(profileRow, "FFDCFCE7");
  moneyCell(profileRow.getCell(2), `Inputs!B${I.monthly}`, true);
  moneyCell(profileRow.getCell(3), `Inputs!B${I.annual}`, true);
  profileRow.getCell(2).font = { bold: true, color: { argb: "FF1D4ED8" } };
  profileRow.getCell(3).font = { bold: true, color: { argb: "FF1D4ED8" } };

  // ═══════════════════════════════════════════════════════════
  // Sheet: Component Basis (explanations)
  // ═══════════════════════════════════════════════════════════
  const ws3 = wb.addWorksheet("Component Basis", {
    views: [{ state: "frozen", ySplit: 1 }],
  });
  ws3.columns = [
    { header: "Section", width: 22 },
    { header: "Component", width: 28 },
    { header: "Monthly (₹)", width: 14 },
    { header: "Annual formula", width: 16 },
    { header: "How calculated / basis", width: 55 },
    { header: "PF Applicable", width: 14 },
    { header: "Gratuity Applicable", width: 16 },
  ];
  styleHeaderRow(ws3.getRow(1));
  (breakdown.componentCalculations || []).forEach((c) => {
    const r = ws3.addRow([
      c.section,
      c.name,
      Number(c.monthly) || 0,
      null,
      c.basis,
      c.pfApplicable ? "Yes" : "No",
      c.gratuityApplicable ? "Yes" : "No",
    ]);
    r.getCell(3).numFmt = "#,##0";
    moneyCell(r.getCell(4), `C${r.number}*12`, true);
    r.getCell(4).font = { color: { argb: "FF1D4ED8" } };
    r.getCell(5).alignment = { wrapText: true };
  });

  const addStatBasis = (section, name, monthly, how) => {
    const r = ws3.addRow([section, name, Number(monthly) || 0, null, how, "—", "—"]);
    r.getCell(3).numFmt = "#,##0";
    moneyCell(r.getCell(4), `C${r.number}*12`, true);
    r.getCell(4).font = { color: { argb: "FF1D4ED8" } };
    r.getCell(5).alignment = { wrapText: true };
  };

  addStatBasis(
    "Employer (Statutory)",
    "PF (Employer)",
    null,
    `Excel: Inputs!B${I.pfErTotal} = Employer Share + Admin + EDLI on PF Wage Used`
  );
  // Fix monthly for display - use formula ref note only; put 0 and point to Inputs
  ws3.lastRow.getCell(3).value = { formula: `Inputs!B${I.pfErTotal}` };
  ws3.lastRow.getCell(3).numFmt = "#,##0";
  ws3.lastRow.getCell(3).font = { color: { argb: "FF1D4ED8" } };

  addStatBasis(
    "Employer (Statutory)",
    "Gratuity",
    null,
    `Excel: Inputs!B${I.gratAmt} = Gratuity Base × Rate%`
  );
  ws3.lastRow.getCell(3).value = { formula: `Inputs!B${I.gratAmt}` };
  ws3.lastRow.getCell(3).numFmt = "#,##0";
  ws3.lastRow.getCell(3).font = { color: { argb: "FF1D4ED8" } };

  addStatBasis(
    "Deductions (Statutory)",
    "PF (Employee)",
    null,
    `Excel: Inputs!B${I.pfEmpAmt} = PF Wage Used × Employee Share%`
  );
  ws3.lastRow.getCell(3).value = { formula: `Inputs!B${I.pfEmpAmt}` };
  ws3.lastRow.getCell(3).numFmt = "#,##0";
  ws3.lastRow.getCell(3).font = { color: { argb: "FF1D4ED8" } };

  addStatBasis(
    "Deductions (Statutory)",
    "Professional Tax (PT)",
    null,
    `Excel: Inputs!B${I.ptFinal} from PT slabs (${info.ptState})`
  );
  ws3.lastRow.getCell(3).value = { formula: `Inputs!B${I.ptFinal}` };
  ws3.lastRow.getCell(3).numFmt = "#,##0";
  ws3.lastRow.getCell(3).font = { color: { argb: "FF1D4ED8" } };

  // Legend sheet
  const wl = wb.addWorksheet("Legend");
  wl.columns = [{ width: 28 }, { width: 80 }];
  wl.addRow(["PaceHRM Salary Breakdown — Excel formulas"]).font = { bold: true, size: 14 };
  wl.addRow([]);
  wl.addRow(["Colour", "Meaning"]).font = { bold: true };
  wl.addRow(["Yellow cells", "Editable inputs (change these to recalculate)"]);
  wl.addRow(["Blue values", "Live Excel formulas (do not type over)"]);
  wl.addRow([]);
  wl.addRow(["Sheet", "Purpose"]).font = { bold: true };
  wl.addRow(["Inputs", "Profile CTC, flags, wage bases, statutory rates, and PF/ESI/Gratuity formulas"]);
  wl.addRow(["Salary Breakdown", "E / R / B / D lines; Annual = Monthly×12; totals = SUM; statutory lines link to Inputs"]);
  wl.addRow(["Component Basis", "Plain-language explanation of how each component was derived"]);
  wl.addRow([]);
  wl.addRow([
    "Tip",
    "Change Profile Annual or PF Wage Base / Ceiling PF on Inputs — PF Employee, PF Employer (incl. Admin+EDLI), Gratuity and ESI recalculate automatically.",
  ]);

  const filename = `${buildFileBase(employee, info.structureName)}.xlsx`;
  const buffer = await wb.xlsx.writeBuffer();
  downloadBlob(
    new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    filename
  );
  return filename;
}

/**
 * @returns {string} filename
 */
export function exportSalaryBreakdownToPdf({ employee, structure, breakdown }) {
  if (!breakdown) throw new Error("No breakdown to export");
  const info = headerInfo(employee, structure, breakdown);
  const basis = info.basis;
  const rates = basis.rates || {};
  const pfDetail = basis.pfEmployerDetail || {};
  const t = breakdown.totals || {};

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  let y = 14;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(30, 58, 95);
  doc.text("Salary Structure Breakdown", 14, y);
  y += 7;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(60, 60, 60);
  const headerLines = [
    `Employee: ${info.empName} (${info.empCode})`,
    `Structure: ${info.structureName}  ·  Mode: ${info.mode}`,
    `Profile: ${INR(info.profileAnnual)} / year  (${INR(info.profileMonthly)} / month)`,
    `Effective month: ${info.effectiveMonth}  ·  PT State: ${info.ptState}`,
    `Statutory: ${info.statutoryLine}`,
    `Exported: ${new Date().toLocaleString("en-IN")}`,
  ];
  headerLines.forEach((line) => {
    doc.text(line, 14, y);
    y += 4.5;
  });
  y += 2;

  const moneyRows = [];
  collectSectionRows(breakdown).forEach((row) => {
    if (row.kind === "section") {
      moneyRows.push([
        { content: row.name, colSpan: 3, styles: { fontStyle: "bold", fillColor: [219, 234, 254] } },
      ]);
    } else {
      moneyRows.push([
        {
          content: row.name,
          styles: row.kind === "total" ? { fontStyle: "bold", fillColor: [226, 232, 240] } : {},
        },
        {
          content: INR(row.monthly),
          styles: {
            halign: "right",
            fontStyle: row.kind === "total" ? "bold" : "normal",
            fillColor: row.kind === "total" ? [226, 232, 240] : undefined,
          },
        },
        {
          content: INR(row.annual),
          styles: {
            halign: "right",
            fontStyle: row.kind === "total" ? "bold" : "normal",
            fillColor: row.kind === "total" ? [226, 232, 240] : undefined,
          },
        },
      ]);
    }
  });

  autoTable(doc, {
    startY: y,
    head: [["Component", "Monthly", "Annual"]],
    body: moneyRows,
    theme: "grid",
    headStyles: {
      fillColor: [30, 58, 95],
      textColor: 255,
      fontStyle: "bold",
      fontSize: 9,
    },
    bodyStyles: { fontSize: 8, textColor: [40, 40, 40] },
    styles: { cellPadding: 1.8 },
    columnStyles: {
      0: { cellWidth: 90 },
      1: { cellWidth: 45, halign: "right" },
      2: { cellWidth: 45, halign: "right" },
    },
    margin: { left: 14, right: 14 },
  });

  y = doc.lastAutoTable.finalY + 6;
  autoTable(doc, {
    startY: y,
    head: [["Summary", "Monthly", "Annual"]],
    body: [
      ["Gross Salary (E + R)", INR(t.gross), INR(t.grossAnnual)],
      ["Net Pay (E + R − D)", INR(t.net), INR(t.netAnnual)],
      ["Total CTC (E + R + B)", INR(t.actualCtcMonthly), INR(t.actualCtcAnnual)],
      ["Profile CTC Target", INR(info.profileMonthly), INR(info.profileAnnual)],
    ],
    theme: "grid",
    headStyles: { fillColor: [22, 101, 52], textColor: 255, fontSize: 9 },
    bodyStyles: { fontSize: 8, fontStyle: "bold" },
    margin: { left: 14, right: 14 },
  });

  doc.addPage();
  y = 14;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(30, 58, 95);
  doc.text("Calculation Basis", 14, y);
  y += 6;

  autoTable(doc, {
    startY: y,
    head: [["Parameter", "Value / Rule"]],
    body: [
      ["Mode", info.mode],
      ["PF Wage Base", INR(basis.pfWageBase)],
      ["PF Wage Used", INR(basis.pfWageUsed)],
      ["Ceiling note", pfDetail.note || "—"],
      ["Gratuity Wage Base", INR(basis.gratuityWageBase)],
      ["Gross for ESI / PT", INR(basis.grossForEsiPt)],
      ["PF Employee %", `${rates.pfEmployeeShare}%`],
      ["PF Employer % + Admin % + EDLI %", `${rates.pfEmployerShare}% + ${rates.pfAdminCharges}% + ${rates.pfEdliRate}% (EDLI max ₹75)`],
      ["PF (Employer) split", `Share ${INR(pfDetail.employerShare)} + Admin ${INR(pfDetail.adminCharges)} + EDLI ${INR(pfDetail.edliCharges)} = ${INR(pfDetail.total)}`],
      ["Gratuity Rate", `${rates.gratuityRate}%`],
      ["ESI rates / cutoff", `${rates.esiEmployeeShare}% / ${rates.esiEmployerShare}% · cutoff ${INR(rates.esiCutoff)}`],
      ["PT State", info.ptState],
      ["Minimum Wages", basis.minimumWageAmount != null ? INR(basis.minimumWageAmount) : "Not applied"],
    ],
    theme: "grid",
    headStyles: { fillColor: [30, 58, 95], textColor: 255, fontSize: 9 },
    bodyStyles: { fontSize: 8 },
    columnStyles: { 0: { cellWidth: 55, fontStyle: "bold" }, 1: { cellWidth: 125 } },
    margin: { left: 14, right: 14 },
  });

  y = doc.lastAutoTable.finalY + 8;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("How each component was calculated", 14, y);
  y += 2;

  const howBody = (breakdown.componentCalculations || []).map((c) => [
    c.name,
    INR(c.monthly),
    c.basis,
  ]);
  (breakdown.statutoryEmployerList || []).forEach((item) => {
    let how = "Statutory employer";
    if (String(item.name).includes("PF")) {
      how = `Share ${rates.pfEmployerShare}% + Admin ${rates.pfAdminCharges}% + EDLI on wage ${INR(basis.pfWageUsed)}`;
    } else if (String(item.name).includes("Gratuity")) {
      how = `${rates.gratuityRate}% of ${INR(basis.gratuityWageBase)}`;
    }
    howBody.push([item.name, INR(item.amount), how]);
  });
  (breakdown.statutoryDeductionsList || []).forEach((item) => {
    let how = "Statutory deduction";
    if (String(item.name).includes("PF")) {
      how = `${rates.pfEmployeeShare}% of PF wage used ${INR(basis.pfWageUsed)}`;
    } else if (String(item.name).includes("PT")) {
      how = `PT slabs — ${info.ptState}`;
    }
    howBody.push([item.name, INR(item.amount), how]);
  });

  autoTable(doc, {
    startY: y + 2,
    head: [["Component", "Monthly", "Calculation basis"]],
    body: howBody,
    theme: "grid",
    headStyles: { fillColor: [30, 58, 95], textColor: 255, fontSize: 8 },
    bodyStyles: { fontSize: 7.5 },
    columnStyles: {
      0: { cellWidth: 42 },
      1: { cellWidth: 28, halign: "right" },
      2: { cellWidth: 110 },
    },
    margin: { left: 14, right: 14 },
  });

  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(140);
    doc.text(
      `PaceHRM · ${info.structureName} · Page ${i} of ${pageCount}`,
      pageW / 2,
      doc.internal.pageSize.getHeight() - 8,
      { align: "center" }
    );
  }

  const filename = `${buildFileBase(employee, info.structureName)}.pdf`;
  doc.save(filename);
  return filename;
}
